<template>
  <div>
    <category-layout>
      <section class="about-item-area">
        <div class="container">
          <div class="row">
            <div class="col-lg-12">
              <div class="about-author-content pt-15">
                <nav aria-label="breadcrumb">
                  <ol class="breadcrumb">
                    <li class="breadcrumb-item">
                      <router-link to="/">首页</router-link>
                    </li>
                    <li v-if="channel" class="breadcrumb-item active" aria-current="page">
                      {{ channel | channelToLabel }}
                    </li>
                    <li v-else-if="keyword" class="breadcrumb-item active" aria-current="page">
                      {{ keyword }}
                    </li> 
                  </ol>
                </nav>
              </div>
            </div>
            <div class="col-lg-8">
              <div class="about-tab-btn mt-40">
                <div class="archive-btn">
                  <ul>
                    <li>
                      <span v-if="channel">{{ channel | channelToLabel }}</span>
                    </li>
                  </ul>
                </div>
                <div class="about-post-items">
                  <div class="row">
                    <div class="col-lg-12" v-if="articles.length">
                      <div v-for="article in articles" :key="article.id">
                        <div class="business-post-item mb-40">
                          <div class="row">
                            <div class="col-lg-6 col-md-6">
                              <div class="business-post-thumb">
                                <img
                                  :src="article.coverurl"
                                  :alt="article.title"
                                />
                              </div>
                            </div>
                            <div class="col-lg-6 col-md-6">
                              <div class="trending-news-item">
                                <div class="trending-news-content">
                                  <div class="post-meta">
                                    <div class="meta-categories" v-if="article.channel">
                                      <router-link :to="`/articles?channel=${article.channel}`">
                                        {{ String(article.channel) | channelToLabel }}
                                      </router-link>
                                    </div>
                                    <div class="meta-date">
                                      <span>{{ formatTimestampToDateTime(article.createdat) }}</span>
                                    </div>
                                  </div>
                                  <h3 class="title">
                                    <router-link :to="`/articles/${article.id}`">{{ article.title }}</router-link>
                                  </h3>
                                  <p class="text three-line-ellipsis">
                                    {{ article.summary }}
                                  </p>
                                  <router-link :to="`/articles/${article.id}`">查看更多</router-link>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div v-else>
                      <div class="col-lg-12">
                        <div class="error-thumb text-center">
                          <img src="@/assets/images/empty.jpg" alt="" />
                        </div>
                      </div>
                    </div>
                    <div class="col-lg-12">
                      <div v-if="totalPages > 1" class="pagination-item">
                        <nav aria-label="Page navigation example">
                          <ul class="pagination">
                            <!-- 上一页按钮 -->
                            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                              <a class="page-link" @click="changePage(currentPage - 1)" href="#" aria-label="Previous">
                                <span aria-hidden="true"><i class="fas fa-caret-left"></i></span>
                              </a>
                            </li>
                            <!-- 页码按钮 -->
                            <li 
                              v-for="page in visiblePages" 
                              :key="page" 
                              class="page-item" 
                              :class="{ active: page === currentPage }"
                            >
                              <a class="page-link" @click="changePage(page)" href="#">{{ page }}</a>
                            </li>
                            <!-- 下一页按钮 -->
                            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                              <a class="page-link" @click="changePage(currentPage + 1)" href="#" aria-label="Next">
                                <span aria-hidden="true"><i class="fas fa-caret-right"></i></span>
                              </a>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-lg-4">
              <style-one :datas="posts" :sharePost="false" />
            </div>
          </div>
        </div>
      </section>
    </category-layout>
  </div>
</template>

<script>
import CategoryLayout from "./CategoryLayout.vue";
import StyleOne from "../Utility/Sidebar/StyleOne.vue";
import { getArticles } from "@/api/article";
import { formatTimestampToDateTime } from '@/utils/dateUtil';

export default {
  components: { CategoryLayout, StyleOne },
  inject: ['dataWrapper', 'fetchStatus'],
  data() {
    return {
      posts: [],
      channel: this.$route.query.channel || '',
      articles: [],
      keyword: this.$route.query.keyword || '',
      // 每页显示的文章数量
      pageSize: 10,
      // 当前页码
      currentPage: 1,
      // 总文章数量
      total: 0,
      // 最多显示的页码数
      maxVisiblePages: 5,
    };
  },
  computed: {
    // 计算总页数
    totalPages() {
      return Math.ceil(this.total / this.pageSize);
    },
    // 计算可见的页码
    visiblePages() {
      const startPage = Math.max(1, this.currentPage - Math.floor(this.maxVisiblePages / 2));
      const endPage = Math.min(this.totalPages, startPage + this.maxVisiblePages - 1);
      return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
    }
  },
  watch: {
    '$route.query.channel': {
      handler(newChannel) {
        this.channel = newChannel;
        this.resetPagination();
        this.fetchArticles();
      }
    },
    '$route.query.keyword': {
      handler(newKeyword) {
        this.keyword = newKeyword;
        this.resetPagination();
        this.fetchArticles();
      },
      immediate: true
    },
    'fetchStatus.isFetched': {
      handler(newVal) {
        if (newVal) {
          this.posts = Array.isArray(this.dataWrapper.articles) 
            ? this.dataWrapper.articles.slice(0, 20) 
            : [];
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    async fetchArticles() {
      try {
        const filter = {};
        if (this.channel) {
          filter.channel = this.channel;
        }
        if (this.keyword) {
          filter.keyword = this.keyword;
        }
        const response = await getArticles({
          filter,
        },this.currentPage,this.pageSize);
        if (response && response.list) {
          this.articles = response.list;
          this.total = response.data.pagination.total;
        } else {
          console.error('响应数据格式不正确');
        }
      } catch (error) {
        console.error('获取文章数据失败:', error);
      }
    },
    // 切换页码的方法
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchArticles();
      }
    },
    // 重置分页信息
    resetPagination() {
      this.currentPage = 1;
    },
    formatTimestampToDateTime
  },
  created() {
    console.log('Articles 组件创建');
    this.fetchArticles();
  },
  beforeDestroy() {
    // console.log('Articles 组件销毁');
  }
};
</script>

<style scoped>
.three-line-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3; /* 显示三行 */
}
</style>
